// components/layout/NavMenu/NavDrawerComponents.tsx
import { forwardRef, MouseEvent, ReactNode, ReactElement } from 'react';
import { Link as RouterLink, LinkProps, useLocation } from 'react-router-dom';
import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Chip,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LogoutIcon from '@mui/icons-material/Logout';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { APP_NAME } from '../../../config/constants';

// CustomLink props
interface CustomLinkProps extends Omit<LinkProps, 'onClick'> {
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

// CustomLink for proper navigation
export const CustomLink = forwardRef<HTMLAnchorElement, CustomLinkProps>((props, ref) => {
  const { onClick, ...rest } = props;
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
      return;
    }
    if (onClick) onClick(event);
  };
  return <RouterLink ref={ref} {...rest} onClick={handleClick} />;
});

CustomLink.displayName = 'CustomLink';

// NotificationBadge props
export interface NotificationBadgeProps {
  count: number;
  children: ReactNode;
}

// Notification badge component
export function NotificationBadge({ count, children }: NotificationBadgeProps): ReactElement {
  if (!count || count === 0) return <>{children}</>;

  return (
    <Badge
      badgeContent={count}
      color="error"
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: '#FF3366',
          color: '#FFF',
          fontWeight: 700,
          fontSize: '0.7rem',
          minWidth: 18,
          height: 18,
        },
      }}
    >
      {children}
    </Badge>
  );
}

// SectionHeader props
export interface SectionHeaderProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  notificationCount?: number;
  color?: string;
}

// Section header component for collapsible sections
export function SectionHeader({ title, expanded, onToggle, notificationCount = 0, color = '#4A5568' }: SectionHeaderProps): ReactElement {
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
        borderRadius: '8px',
        mx: 1,
        mb: 0.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </Typography>
        {notificationCount > 0 && (
          <Chip
            label={notificationCount}
            size="small"
            sx={{
              height: 18,
              minWidth: 18,
              backgroundColor: '#FF3366',
              color: '#FFF',
              fontWeight: 700,
              fontSize: '0.65rem',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
      </Box>
      <IconButton size="small" sx={{ color: '#8892A0', p: 0 }} aria-label={expanded ? `Collapse ${title} section` : `Expand ${title} section`}>
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
}

// DrawerHeader props
export interface DrawerHeaderProps {
  onToggle: () => void;
}

// Drawer header component
export function DrawerHeader({ onToggle }: DrawerHeaderProps): ReactElement {
  return (
    <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <Typography
        component={CustomLink}
        to="/landing"
        onClick={onToggle}
        sx={{
          fontFamily: '"Rajdhani", sans-serif',
          fontWeight: 700,
          fontSize: '1.5rem',
          background: 'linear-gradient(135deg, #00FFD4 0%, #00D4FF 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textDecoration: 'none',
          cursor: 'pointer',
          display: 'block',
          '&:hover': { opacity: 0.8 },
        }}
      >
        {APP_NAME}
      </Typography>
      <Typography sx={{ color: '#8892A0', fontSize: '0.8rem' }}>
        Tournament Management
      </Typography>
    </Box>
  );
}

// NavItem props
export interface NavItemProps {
  to: string;
  icon: ReactNode;
  primary: string;
  secondary?: string | null;
  iconColor: string;
  onClick?: () => void;
}

// Navigation item component
export function NavItem({ to, icon, primary, secondary, iconColor, onClick }: NavItemProps): ReactElement {
  return (
    <ListItem disablePadding>
      <ListItemButton component={CustomLink} to={to} onClick={onClick} sx={{ mx: 1, borderRadius: '8px' }}>
        <ListItemIcon sx={{ color: iconColor, minWidth: 40 }}>{icon}</ListItemIcon>
        <ListItemText
          primary={primary}
          secondary={secondary}
          primaryTypographyProps={{ color: '#FFF' }}
          secondaryTypographyProps={{ color: '#8892A0', fontSize: '0.7rem' }}
        />
      </ListItemButton>
    </ListItem>
  );
}

// NotificationAlertItem props
export interface NotificationAlertItemProps {
  to: string;
  icon: ReactNode;
  primary: string;
  onClick?: () => void;
  color: string;
  bgColor: string;
}

// Notification alert item (for collapsed sections)
export function NotificationAlertItem({ to, icon, primary, onClick, color, bgColor }: NotificationAlertItemProps): ReactElement {
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        component={CustomLink}
        to={to}
        onClick={onClick}
        sx={{
          mx: 1,
          borderRadius: '8px',
          backgroundColor: bgColor,
          border: `1px solid ${color}`,
          py: 0.75,
        }}
      >
        <ListItemIcon sx={{ color, minWidth: 36 }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={primary}
          primaryTypographyProps={{ color, fontSize: '0.85rem', fontWeight: 600 }}
        />
      </ListItemButton>
    </ListItem>
  );
}

// FooterItems props
export interface FooterItemsProps {
  onBreak: boolean;
  onToggleBreak: () => void;
  onLogout: () => void;
  variant?: 'desktop' | 'mobile';
}

// Footer items component
export function FooterItems({ onBreak, onToggleBreak, onLogout, variant = 'desktop' }: FooterItemsProps): ReactElement {
  const location = useLocation();
  const isMobile = variant === 'mobile';

  return (
    <Box sx={isMobile ? { mt: 2 } : { p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      {/* Break Status */}
      <ListItemButton
        onClick={onToggleBreak}
        sx={{
          borderRadius: '8px',
          mb: 1,
          mx: isMobile ? 1 : 0,
          backgroundColor: onBreak ? 'rgba(255, 51, 102, 0.15)' : 'rgba(0, 255, 136, 0.15)',
          border: `1px solid ${onBreak ? '#FF3366' : '#00FF88'}`,
        }}
      >
        <ListItemIcon sx={{ color: onBreak ? '#FF3366' : '#00FF88', minWidth: 40 }}>
          {onBreak ? <RestaurantIcon /> : <CheckCircleOutlineIcon />}
        </ListItemIcon>
        <ListItemText
          primary={onBreak ? 'On Break' : 'Active'}
          primaryTypographyProps={{ fontWeight: 600, color: onBreak ? '#FF3366' : '#00FF88' }}
        />
      </ListItemButton>

      {/* Settings */}
      <ListItemButton
        component={CustomLink}
        to="/user-settings"
        sx={{
          borderRadius: '8px',
          mb: 1,
          mx: isMobile ? 1 : 0,
          backgroundColor: location.pathname === '/user-settings' ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
          border: location.pathname === '/user-settings' ? '1px solid #00D4FF' : '1px solid transparent',
          '&:hover': { backgroundColor: 'rgba(0, 212, 255, 0.1)' },
        }}
      >
        <ListItemIcon sx={{ color: '#00D4FF', minWidth: 40 }}><SettingsIcon /></ListItemIcon>
        <ListItemText primary="Settings" primaryTypographyProps={{ fontWeight: 600 }} />
      </ListItemButton>

      {/* Feedback */}
      <ListItemButton
        component={CustomLink}
        to="/feedback"
        sx={{
          borderRadius: '8px',
          mb: 1,
          mx: isMobile ? 1 : 0,
          backgroundColor: location.pathname === '/feedback' ? 'rgba(136, 146, 160, 0.15)' : 'transparent',
          border: location.pathname === '/feedback' ? '1px solid #8892A0' : '1px solid transparent',
          '&:hover': { backgroundColor: 'rgba(136, 146, 160, 0.1)' },
        }}
      >
        <ListItemIcon sx={{ color: '#8892A0', minWidth: 40 }}><FeedbackIcon /></ListItemIcon>
        <ListItemText primary="Feedback" primaryTypographyProps={{ fontWeight: 600, color: '#8892A0' }} />
      </ListItemButton>

      {/* Switch to Player Dashboard. Mirrors the PlayerNavMenu
          equivalent — sits right above Logout, grouped with the
          other 'leave this surface' actions. Always visible for
          authenticated staff users; the destination renders
          gracefully even when no player id is linked. */}
      <ListItemButton
        component={CustomLink}
        to="/player"
        sx={{
          borderRadius: '8px',
          mb: 1,
          mx: isMobile ? 1 : 0,
          backgroundColor: 'rgba(123, 104, 238, 0.10)',
          border: '1px solid rgba(123, 104, 238, 0.3)',
          '&:hover': {
            backgroundColor: 'rgba(123, 104, 238, 0.20)',
            borderColor: 'rgba(123, 104, 238, 0.5)',
          },
        }}
      >
        <ListItemIcon sx={{ color: '#7B68EE', minWidth: 40 }}>
          <SwapHorizIcon />
        </ListItemIcon>
        <ListItemText
          primary="Switch to player dashboard"
          primaryTypographyProps={{ color: '#7B68EE', fontWeight: 600 }}
        />
      </ListItemButton>

      {/* Logout */}
      <ListItemButton
        onClick={onLogout}
        sx={{
          borderRadius: '8px',
          mx: isMobile ? 1 : 0,
          mb: isMobile ? 2 : 0,
          backgroundColor: 'rgba(255, 51, 102, 0.1)',
          border: '1px solid rgba(255, 51, 102, 0.3)',
          '&:hover': { backgroundColor: 'rgba(255, 51, 102, 0.2)' },
        }}
      >
        <ListItemIcon sx={{ color: '#FF3366', minWidth: 40 }}><LogoutIcon /></ListItemIcon>
        <ListItemText primary="Logout" primaryTypographyProps={{ color: '#FF3366', fontWeight: 600 }} />
      </ListItemButton>
    </Box>
  );
}

// Named exports for convenience
const NavDrawerComponents = {
  CustomLink,
  NotificationBadge,
  SectionHeader,
  DrawerHeader,
  NavItem,
  NotificationAlertItem,
  FooterItems,
};

export default NavDrawerComponents;
